import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { IconButton, Badge } from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';

import {
  PodSpacer,
  ProductImage,
  ProductPod,
  ProductRating,
  ProductATC
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';

const HeaderPriceRating = ({
  itemId,
  storeId,
  titleMaxLine,
  showExactPriceOnLoad,
  hidePriceBadge,
  showDisabledATC,
  onSavingsCenterToggle,
  enableATC,
  silentATC,
  disabled,
  sharedSection,
  onAction,
  hideBulkPrice,
  hideLimitPerOrder,
  showPreferredPricingBadge,
  showPreferredPricingBadgeToolTip,
  hideRebates,
  hideSavingsText
}) => {
  return (
    <>
      <PodSpacer minHeight="32px">
        <ProductHeader
          brand="above"
          itemId={itemId}
          titleMaxLine={titleMaxLine}
          disableShopThisCollection
          onAction={onAction}
        />
      </PodSpacer>
      <PodSpacer minHeight="25px" padding={['BOTTOM']}>
        <ProductRating
          itemId={itemId}
        />
      </PodSpacer>
      <PodSpacer minHeight="45px" padding={['TOP']}>
        <Price
          itemId={itemId}
          large={false}
          type="simple"
          hidePromotions={false}
          storeId={storeId}
          onSavingsCenterToggle={onSavingsCenterToggle}
          disableRangePricing={showExactPriceOnLoad}
          hideBadge={hidePriceBadge}
          showPreferredPricingBadge={showPreferredPricingBadge}
          showPreferredPricingBadgeToolTip={showPreferredPricingBadgeToolTip}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hideRebates={hideRebates}
          hideSavingsText={hideSavingsText}
          isSignInToolTip
        />
      </PodSpacer>

      {enableATC && (
        <ProductATC
          showDisabledATC={showDisabledATC}
          disabled={disabled}
          silent={silentATC}
          sharedSection={sharedSection}
          outline
        />
      )}

    </>
  );
};

const KPFAndFulfillment = ({
  itemId,
  storeId,
  hideFulfillment,
  minKpfs,
  showKPF
}) => {
  return (
    <>
      <PodSpacer padding={['TOP']}>
        {showKPF
          && (
            <KeyProductFeatures
              hideImage
              itemId={itemId}
              minfeatures={minKpfs}
              maxfeatures={5}
              simple
              oneColumnGrid
            />
          )}
      </PodSpacer>
      {!hideFulfillment && (
        <PodSpacer minHeight="80px">
          <FulfillmentPodStore itemId={itemId} storeId={storeId} hideForChooseYourOptions />
          <FulfillmentPodShipping itemId={itemId} storeId={storeId} />
        </PodSpacer>
      )}
    </>
  );
};

const Caret = ({ useCaret, onArrowClick, isHDQCSku }) => {
  return useCaret && (
    <div className="product-pod-mini__caret">
      {!isHDQCSku && (
        <IconButton
          onClick={onArrowClick}
          color="brand"
          icon={ArrowForward}
          aria-label="open menu"
        />
      )}
    </div>
  );
};

export const getExperienceName = () => {
  if (!window?.experienceMetadata?.name) {
    return '';
  }

  return window?.experienceMetadata?.name;
};

export const PromotionDrawerProductPod = ({
  itemId,
  storeId,
  position,
  onArrowClick,
  callToActionSection,
  showKPF,
  showDisabledATC,
  hidePriceBadge,
  disabled,
  enableATC,
  inCartQuantity,
  onAction,
  silentATC,
  hideCaret,
  sharedSection,
  showExactPriceOnLoad,
  titleMaxLine = 2,
  hideFulfillment,
  isHDQCSku,
  hideBulkPrice,
  hideLimitPerOrder,
  showPreferredPricingBadge,
  showPreferredPricingBadgeToolTip,
  hideRebates,
  hideSavingsText,
  experienceUsesProductImageCarousel,
  isAnchorItemId,
  type,
}) => {
  const minKpfs = 3;
  const { channel } = useContext(ExperienceContext);
  const isMobileChannel = channel === 'mobile';
  const useCaret = hideFulfillment && !hideCaret;
  const shouldRenderCallToAction = !enableATC && callToActionSection !== null && type ==='pod';
  const currentExperience = getExperienceName();
  const useProductImageCarousel = experienceUsesProductImageCarousel[currentExperience] && isMobileChannel;
  const [useDualImageCarousel, setProductImageCarousel] = useState(useProductImageCarousel);

  const productImageCss = classNames( 'sui-border-primary sui-p-2', {
      'sui-col-span-3' : type === 'pod',
      'sui-col-span-6' : type === 'card',
    }
  );

  const productDetailsCss = classNames( 'sui-border-primary sui-p-2', {
      'sui-col-span-8' : type === 'pod',
      'sui-col-span-6' : type === 'card',
    }
  );

  return (
    <div data-testid={`promotion-products-product-pod-${itemId}`}>
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{ parent: 'promotion-drawer', position }}
        padding={'sui-p-0'}
        render={(pod) => (
          <>
            <div className="sui-grid sui-grid-cols-12">
              <div className={productImageCss}>
                <ProductImage
                  itemId={pod.itemId}
                  showSecondaryImage={useDualImageCarousel}
                  dualImageSwipe={useDualImageCarousel}
                  inCartQuantity={inCartQuantity}
                />
              </div>
              <div className={productDetailsCss}>
                {isAnchorItemId && (
                  <div className="sui-mb-1">
                    <Badge variant="regular" color="info" size="small">Current Item</Badge>
                  </div>
                )}
                <HeaderPriceRating
                  itemId={pod.itemId}
                  storeId={pod.storeId}
                  titleMaxLine={titleMaxLine}
                  showExactPriceOnLoad={showExactPriceOnLoad}
                  hidePriceBadge={hidePriceBadge}
                  showDisabledATC={showDisabledATC}
                  onSavingsCenterToggle={pod.onSavingsCenterToggle}
                  disabled={disabled}
                  enableATC={enableATC}
                  silentATC={silentATC}
                  sharedSection={sharedSection}
                  onAction={onAction}
                  hideBulkPrice={hideBulkPrice}
                  hideLimitPerOrder={hideLimitPerOrder}
                  showPreferredPricingBadge={showPreferredPricingBadge}
                  showPreferredPricingBadgeToolTip={showPreferredPricingBadgeToolTip}
                  hideRebates={hideRebates}
                  hideSavingsText={hideSavingsText}
                />
                <KPFAndFulfillment
                  itemId={pod.itemId}
                  storeId={pod.storeId}
                  hideFulfillment={hideFulfillment}
                  minKpfs={minKpfs}
                  showKPF={showKPF}
                />
              </div>
              {type !== 'card' && (
                <div className="sui-col-span-1 sui-border-1 sui-p-2">
                  <Caret
                    useCaret={useCaret}
                    onArrowClick={onArrowClick}
                    isHDQCSku={isHDQCSku}
                  />
                </div>
              )}
            </div>
            {shouldRenderCallToAction &&
              <PodSpacer minHeight="45px">
                {callToActionSection}
              </PodSpacer>
            }
          </>
        )}
      />
    </div>
  );
};

PromotionDrawerProductPod.displayName = 'PromotionDrawerProductPod';

PromotionDrawerProductPod.dataModel = extend(
  Price,
  ProductHeader,
  ProductImage,
  ProductPod,
  ProductRating,
  ProductATC,
  KeyProductFeatures,
  FulfillmentPodShipping,
  FulfillmentPodStore
);

PromotionDrawerProductPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  onArrowClick: PropTypes.func.isRequired,
  callToActionSection: PropTypes.element,
  showKPF: PropTypes.bool.isRequired,
  showDisabledATC: PropTypes.bool.isRequired,
  hidePriceBadge: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  enableATC: PropTypes.bool.isRequired,
  inCartQuantity: PropTypes.number.isRequired,
  onAction: PropTypes.func.isRequired,
  silentATC: PropTypes.bool.isRequired,
  hideCaret: PropTypes.bool.isRequired,
  sharedSection: PropTypes.string.isRequired,
  showExactPriceOnLoad: PropTypes.bool.isRequired,
  titleMaxLine: PropTypes.number.isRequired,
  hideFulfillment: PropTypes.bool.isRequired,
  isHDQCSku: PropTypes.bool.isRequired,
  hideBulkPrice: PropTypes.bool.isRequired,
  hideLimitPerOrder: PropTypes.bool.isRequired,
  showPreferredPricingBadge: PropTypes.bool.isRequired,
  showPreferredPricingBadgeToolTip: PropTypes.bool.isRequired,
  hideRebates: PropTypes.bool.isRequired,
  hideSavingsText: PropTypes.bool.isRequired,
  experienceUsesProductImageCarousel: PropTypes.object.isRequired,
  isAnchorItemId: PropTypes.bool.isRequired
};

PromotionDrawerProductPod.defaultProps = {
  callToActionSection: null,
};
